import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { SingleValue } from 'react-select';
import { ActorRefFrom, SnapshotFrom } from 'xstate';
import {
  AsyncSelect,
  Button,
  FormActions,
  FormDialog,
  FormLayout,
  FormRow,
  FormSection,
  Select,
  TextInput,
  Toggle,
} from '~/components';
import {
  FacilityForm as AddCustomFacilityForm,
  AddFacilityButton,
  DuplicateFacilityDialog,
  facilityMachine,
} from '~/features';
import { FacilityFormValues } from '~/features/types';
import { API_CALLS } from '~/features/vault/facility/api';
import { useCustomForm } from '~/hooks/useCustomForm';
import {
  AREA_TYPE_OPTIONS,
  CLINICAL_SETTING_CATEGORY_OPTIONS,
  CLINICAL_SETTING_OPTIONS,
  ClinicalSettingCategory,
  emptyEmployerFacilityForm,
  FACILITY_SIZE_OPTIONS,
} from './constants';
import { createEmployerFacilitySchema, EmployerFacilityValues } from './types';
import { toStoredFacilityName } from './utils';

const TAGS = CT.employer.registration;

const FacilitySizeTooltip = () => {
  return (
    <p className="text-sm max-w-sm">
      <b>Size of the medical facility:</b> <br />
      <br />
      <b>Small</b> - Hospitals with 25 beds or less, standalone specialty clinics, urgent care,
      mobile or frontier clinics, nursing homes less than 50 beds, small private practice or small
      medical groups.
      <br />
      <br />
      <b>Medium</b> - community hospitals (25-200 beds), ambulatory surgery centers with moderate
      capacity, skilled nursing facilities w/50-100 beds.
      <br />
      <br />
      <b>Large</b> - Tertiary / Academic Medical Centers (200+ beds), Large Regional Hospitals (200+
      beds), skilled nursing facilities with 100+ beds.
    </p>
  );
};

interface EmployerFacilityFormProps {
  onSubmitSuccess: (data: EmployerFacilityValues) => void;
  facilityRef: ActorRefFrom<typeof facilityMachine>;
  facility?: { facilityName: string; facilityId: number | string | undefined } | undefined;
  isPrimary?: boolean;
  facilityDialogZIndex?: 'default' | 'nested' | 'top';
  defaultValues?: EmployerFacilityValues;
  submitLabel?: string;
  loadFacilityOptions?: (input: string) => Promise<FacilityFormValues[]>;
  /**
   * This form has two kinds of host. Two mount it inside a FormDialog
   * (EmployerFacilitiesPage, and the listings facilities step's nested "Add New
   * Employer Facility" dialog); a dialog owns its own scroll container, so a
   * sticky action bar is correct there and is the default.
   *
   * The other two are the employer registration routes -- v1
   * (features/employer/registration/facility) and v2
   * (features/employer/registration-v2/forms) -- which render it in normal page
   * flow, where `sticky bottom-0` resolves against the viewport and floats the
   * bar over the content below it. Both pass `false`.
   */
  stickyActions?: boolean;
}

/** [MCV-1667] Lets a host read the in-progress (unsubmitted) form values --
 * e.g. to preserve a draft when the user navigates away via an external Back
 * control instead of submitting. */
export interface EmployerFacilityFormHandle {
  getValues: () => EmployerFacilityValues;
}

const selectFacilityModal = (state: SnapshotFrom<typeof facilityMachine>) => state.context.modal;
const selectFacilityForm = (state: SnapshotFrom<typeof facilityMachine>) => state.context.form;
const selectShowDuplicates = (state: SnapshotFrom<typeof facilityMachine>) =>
  state.context.showDuplicates;
const selectDuplicateFacilities = (state: SnapshotFrom<typeof facilityMachine>) =>
  state.context.duplicates;
const selectPendingFacilityName = (state: SnapshotFrom<typeof facilityMachine>) =>
  state.context.pendingValues.label;

export const EmployerFacilityForm = forwardRef<
  EmployerFacilityFormHandle,
  EmployerFacilityFormProps
>(function EmployerFacilityForm(
  {
    onSubmitSuccess,
    facilityRef,
    facility,
    isPrimary,
    facilityDialogZIndex = 'nested',
    defaultValues,
    submitLabel = 'Submit',
    loadFacilityOptions = API_CALLS.get,
    stickyActions = true,
  }: EmployerFacilityFormProps,
  ref
) {
  const facilityModal = useSelector(facilityRef, selectFacilityModal);
  const form = useSelector(facilityRef, selectFacilityForm);
  const showDuplicates = useSelector(facilityRef, selectShowDuplicates);
  const duplicates = useSelector(facilityRef, selectDuplicateFacilities);
  const pendingFacilityName = useSelector(facilityRef, selectPendingFacilityName);

  // [MCV-1518] The required-metadata rule applies to Add Facility only. An
  // existing record carries an id; a new one does not. See ./types.
  const isExistingFacility = defaultValues?.id != null;
  const schema = useMemo(
    () => createEmployerFacilitySchema(isExistingFacility),
    [isExistingFacility]
  );

  const methods = useCustomForm<EmployerFacilityValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyEmployerFacilityForm,
  });
  const { reset, setValue, watch, handleSubmit, getValues } = methods;

  useImperativeHandle(ref, () => ({ getValues }), [getValues]);
  const previousClinicalSettingCategory = useRef<string | undefined>(undefined);

  const handleFacilityClose = () => facilityRef.send({ type: 'ON_CLOSE' });
  const handleFacilityOpen = (values: FacilityFormValues) =>
    facilityRef.send({ type: 'ON_OPEN', values });

  const handleFacilityFormSubmit = (values: FacilityFormValues) =>
    facilityRef.send({ type: 'ON_SUBMIT', values });
  const handleSelectExistingFacility = (selectedFacility: FacilityFormValues) =>
    facilityRef.send({ type: 'ON_SELECT_EXISTING', facility: selectedFacility });
  const handleConfirmCreateFacility = () => facilityRef.send({ type: 'ON_CONFIRM_CREATE' });
  const handleBackToFacilityForm = () => facilityRef.send({ type: 'ON_BACK_TO_FORM' });

  const onSubmitError = (err: unknown) =>
    console.log('There was an error submitting the facility form', err);

  const clinicalSettingCategory = watch('clinicalSettingCategory');

  useEffect(() => {
    const nextValues = {
      ...emptyEmployerFacilityForm,
      ...defaultValues,
    };
    if (isPrimary) {
      nextValues.isPrimary = true;
    }
    if (facility?.facilityName && facility?.facilityId) {
      nextValues.facilityName = {
        label: facility.facilityName,
        value: facility.facilityName,
      };
      nextValues.facilityId = facility.facilityId.toString();
    }
    previousClinicalSettingCategory.current = nextValues.clinicalSettingCategory.value;
    reset(nextValues);
  }, [defaultValues, facility, isPrimary, reset]);

  useEffect(() => {
    const nextCategory = clinicalSettingCategory?.value;
    if (
      nextCategory &&
      previousClinicalSettingCategory.current &&
      previousClinicalSettingCategory.current !== nextCategory
    ) {
      setValue('clinicalSetting', { label: '', value: '' });
    }
    previousClinicalSettingCategory.current = nextCategory;
  }, [clinicalSettingCategory, setValue]);

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
          <FormLayout>
            <FormSection componentsPerLine={1}>
              <FormRow>
                <AsyncSelect
                  name={'facilityName'}
                  cyTag={TAGS.facilityName}
                  label={'Facility Name'}
                  required={!isExistingFacility}
                  cacheOptions={true}
                  loadOptions={loadFacilityOptions}
                  onChange={(
                    option: SingleValue<{ value: string; label: string } & FacilityFormValues>
                  ) => {
                    if (option) {
                      // [MCV-1876] The option label carries the picker's "(City, ST)"
                      // suffix; the stored name is the facility's own name.
                      const storedName = toStoredFacilityName(option);
                      setValue('facilityName', { label: storedName, value: storedName });
                      setValue('facilityId', option.value);
                    } else {
                      setValue('facilityName', { label: '', value: '' });
                      setValue('facilityId', '');
                    }
                  }}
                />
                <AddFacilityButton className="mt-3" handleFacilityOpen={handleFacilityOpen} />
              </FormRow>
              {/* [MCV-1577] `facilityNpi` is a plain sibling field, not a nested
                  section: FormSection already lays every child out on its own
                  12-column row at the shared 20px rhythm, so wrapping this one
                  in a second FormSection only added a redundant grid. */}
              <FormRow>
                <TextInput cyTag={TAGS.facilityNpi} name="facilityNpi" label="Facility NPI" />
              </FormRow>
            </FormSection>
            <FormSection componentsPerLine={2}>
              <FormRow>
                <Select
                  cyTag={TAGS.clinicalSettingCategory}
                  name="clinicalSettingCategory"
                  label="Clinical Setting Category"
                  required={!isExistingFacility}
                  options={CLINICAL_SETTING_CATEGORY_OPTIONS}
                />
              </FormRow>
              <FormRow>
                <Select
                  cyTag={TAGS.clinicalSetting}
                  name="clinicalSetting"
                  label="Clinical Setting"
                  required={!isExistingFacility}
                  placeholder={
                    clinicalSettingCategory?.value
                      ? undefined
                      : 'Select a clinical setting category first'
                  }
                  options={
                    clinicalSettingCategory?.value
                      ? CLINICAL_SETTING_OPTIONS[
                          clinicalSettingCategory.value as ClinicalSettingCategory
                        ]
                      : []
                  }
                />
              </FormRow>
            </FormSection>
            <FormSection componentsPerLine={2}>
              <FormRow>
                <Select
                  cyTag={TAGS.facilitySize}
                  name="facilitySize"
                  label="Facility Size"
                  required={!isExistingFacility}
                  tooltip={<FacilitySizeTooltip />}
                  options={FACILITY_SIZE_OPTIONS}
                />
              </FormRow>
              <FormRow>
                <Select
                  cyTag={TAGS.areaType}
                  name="areaType"
                  label="Area Type"
                  required={!isExistingFacility}
                  options={AREA_TYPE_OPTIONS}
                />
              </FormRow>
            </FormSection>
            <FormSection componentsPerLine={1}>
              <FormRow>
                <Toggle
                  cyTag={`${TAGS.facilityName}-primary`}
                  name="isPrimary"
                  label="Primary Facility"
                />
              </FormRow>
            </FormSection>
            <FormActions sticky={stickyActions}>
              <Button cyTag={TAGS.submitButton} label={submitLabel} type="submit" />
            </FormActions>
          </FormLayout>
        </form>
      </FormProvider>
      <FormDialog
        title={'Add New Facility'}
        editing={false}
        creating={facilityModal}
        onClose={handleFacilityClose}
        zIndex={facilityDialogZIndex}
      >
        <AddCustomFacilityForm
          handleFacilityFormSubmit={handleFacilityFormSubmit}
          facilityForm={form}
        />
      </FormDialog>
      <DuplicateFacilityDialog
        open={showDuplicates}
        duplicates={duplicates}
        pendingName={pendingFacilityName}
        onSelectExisting={handleSelectExistingFacility}
        onConfirmCreate={handleConfirmCreateFacility}
        onBack={handleBackToFacilityForm}
        zIndex={facilityDialogZIndex}
      />
    </>
  );
});
